import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { read } from "hooks/useSession.ts";
import { models } from "types/api/viewModels.ts";
import { CampaignAllocation } from "features/MarketingMaterials/api/campaignApi.ts";
import { getCampaignAllocationKey } from "features/MarketingMaterials/utils/campaignSelection.ts";

export interface CartShippingDetailsState {
	poNumber: string;
	jobName: string;
	address: models["ShipToViewModel"] | null;
	phoneNumber: string;
	email: string;
}

const initialShippingDetails: CartShippingDetailsState = {
	poNumber: "",
	jobName: "",
	address: null,
	phoneNumber: "",
	email: ""
};

export interface MarketingMaterialsCartState {
	cartItems: models["ShoppingCartItem"][];
	confirmedItems: models["ShoppingCartItem"][];
	campaignItems: CampaignAllocation[];
	selectedCampaignKeys: string[];
	selectedMerchAccount: models["CustomerAccountViewModel"] | null;
	isMerchAccountSelectionError: boolean;
	shippingDetails: CartShippingDetailsState;
	addedItemName: string | null;
	isAddToCartSnackbarOpen: boolean;
	validation: models["ShoppingCartValidation"] | null;
	confirmationNumber?: string | null;
}

interface OptimisticQuantityPayload {
	item: models["MerchandiseProductViewModel"] | models["ShoppingCartItem"];
	quantityChange: number;
}

const persistedCart = read("_marketingMaterialsCart");

const initialState: MarketingMaterialsCartState = {
	cartItems: [],
	campaignItems: [],
	selectedCampaignKeys: [],
	confirmedItems: [],
	selectedMerchAccount: null,
	shippingDetails: { ...initialShippingDetails },
	addedItemName: null,
	isAddToCartSnackbarOpen: false,
	confirmationNumber: null,
	...persistedCart,
	isMerchAccountSelectionError: false,
	validation: null
};

const marketingMaterialsCartSlice = createSlice({
	name: "marketingMaterialsCart",
	initialState,
	reducers: {
		setConfirmationNumber: (state, action: PayloadAction<string | null>) => {
			state.confirmationNumber = action.payload;
			if (action.payload) {
				state.confirmedItems = [...state.cartItems];
				state.cartItems = [];
			}
		},
		setCartItems: (state, action: PayloadAction<models["ShoppingCartItem"][]>) => {
			state.cartItems = action.payload;
			// state.validation = null;
		},
		setCampaignItems: (state, action: PayloadAction<CampaignAllocation[]>) => {
			state.campaignItems = action.payload;
			const availableKeys = new Set(action.payload.map((item) => getCampaignAllocationKey(item)));
			state.selectedCampaignKeys = state.selectedCampaignKeys.filter((key) => availableKeys.has(key));
		},
		toggleCampaignSelection: (state, action: PayloadAction<string>) => {
			const index = state.selectedCampaignKeys.indexOf(action.payload);
			if (index === -1) state.selectedCampaignKeys.push(action.payload);
			else state.selectedCampaignKeys.splice(index, 1);
		},
		clearCampaignSelection: (state) => {
			state.selectedCampaignKeys = [];
		},
		updateItemQuantity: (state, action: PayloadAction<OptimisticQuantityPayload>) => {
			const { item, quantityChange } = action.payload;
			state.validation = null;

			if (!item || !Number.isInteger(quantityChange) || quantityChange === 0) {
				return;
			}

			const existingItemIndex = state.cartItems.findIndex(
				(cartItem) =>
					(cartItem.id && item.id && cartItem.id === item.id) ||
					(cartItem.sku && item.sku && cartItem.sku === item.sku)
			);

			if (existingItemIndex === -1) {
				if (quantityChange > 0) {
					state.cartItems.push({
						id: item.id,
						name: item.name,
						sku: item.sku,
						description: item.description,
						listPrice: item.listPrice,
						images: item.images,
						quantityOrdered: quantityChange
					});
				}
				return;
			}

			const existingItem = state.cartItems[existingItemIndex];
			const nextQuantity = quantityChange;

			if (nextQuantity <= 0) {
				state.cartItems.splice(existingItemIndex, 1);
				return;
			}

			state.cartItems[existingItemIndex] = {
				...existingItem,
				id: item.id ?? existingItem?.id,
				name: item.name ?? existingItem?.name,
				sku: item.sku ?? existingItem?.sku,
				description: item.description ?? existingItem?.description,
				listPrice: item.listPrice ?? existingItem?.listPrice,
				images: item.images ?? existingItem?.images,
				quantityOrdered: nextQuantity
			};
		},
		removeCartItem: (state, action: PayloadAction<models["ShoppingCartItem"]>) => {
			const { id, sku } = action.payload;
			state.validation = null;
			state.cartItems = state.cartItems.filter((item) => !((id && item.id === id) || (sku && item.sku === sku)));
		},
		setValidation: (state, action: PayloadAction<models["ShoppingCartValidation"] | null>) => {
			state.validation = action.payload;
		},
		clearValidation: (state) => {
			state.validation = null;
		},
		setSelectedMerchAccount: (state, action: PayloadAction<models["CustomerAccountViewModel"] | null>) => {
			state.selectedMerchAccount = action.payload;
			state.isMerchAccountSelectionError = false;
			state.validation = null;
		},
		setMerchAccountSelectionError: (state, action: PayloadAction<boolean>) => {
			state.isMerchAccountSelectionError = action.payload;
		},
		setShippingDetails: (state, action: PayloadAction<CartShippingDetailsState>) => {
			state.shippingDetails = action.payload;
		},
		showAddToCartSnackbar: (state, action: PayloadAction<string>) => {
			state.addedItemName = action.payload;
			state.isAddToCartSnackbarOpen = true;
		},
		hideAddToCartSnackbar: (state) => {
			state.isAddToCartSnackbarOpen = false;
			state.addedItemName = null;
		},
		resetCart: (state) => {
			state.confirmedItems = [];
			state.cartItems = [];
			state.campaignItems = [];
			state.selectedCampaignKeys = [];
			state.selectedMerchAccount = null;
			state.isMerchAccountSelectionError = false;
			state.shippingDetails = { ...initialShippingDetails };
			state.addedItemName = null;
			state.isAddToCartSnackbarOpen = false;
			state.validation = null;
			state.confirmationNumber = null;
		}
	}
});

export const marketingMaterialsCartActions = marketingMaterialsCartSlice.actions;
export default marketingMaterialsCartSlice.reducer;
